import {
  RatingEmpty as RatingEmptyIcon,
  RatingFull as RatingFullIcon,
} from '@mos-cat/ds-icons';
import { createComponent, createHook } from 'reakit-system';
import { Box, useBox } from '../../Box/Box';
import { ButtonHTMLProps, ButtonOptions } from '../../Button/Button';
import { useText } from '../../Text/Text';
import { useFocusable } from '../../hooks/useFocusable';
import pxToRem from '../../utils/pxToRem';

export type StarOptions = Omit<ButtonOptions, 'kind' | 'vertSize'> & {
  rating: number;
  selectedRating?: number;
};
export type StarHTMLProps = Omit<ButtonHTMLProps, 'children'>;
export type StarProps = StarOptions & StarHTMLProps;

export const useStar = createHook<StarOptions, StarHTMLProps>({
  keys: ['rating', 'selectedRating'],
  compose: [useBox, useText, useFocusable],
  useOptions(options) {
    let bgColor = ['secondaryBtnBg', 'transparent'];
    return {
      ...options,
      bg: bgColor,
      border: 1,
      borderColor: bgColor,
      borderRadius: 'starBorderRadius',
      color: ['secondaryBtnText', 'borderlessBtnText'],
      flexGrow: [1, 0],
      textAlign: 'center',
      fontSize: ['fs-16', pxToRem(38 / 1.5)],
      p: [pxToRem(11), 'sp-0'],
      styles: {
        ...options.styles,
        '&:hover': {
          borderColor: ['secondaryBtnHoverBorder', 'transparent'],
          bg: ['secondaryBtnHoverBg', 'transparent'],
          color: ['secondaryBtnHoverText', 'borderlessBtnHoverText'],
        },
        '&:active': {
          borderColor: ['secondaryBtnActiveBorder', 'transparent'],
          bg: ['secondaryBtnActiveBg', 'transparent'],
          color: ['secondaryBtnActiveText', 'borderlessBtnActiveText'],
        },
      },
    };
  },
  useProps(options, props) {
    let Icon =
      options.rating <= (options.selectedRating || 0)
        ? RatingFullIcon
        : RatingEmptyIcon;
    return {
      ...props,
      children: <Box as={Icon} display="inline-block" />,
      type: 'button',
    };
  },
});

export const Star = createComponent({
  as: 'button',
  useHook: useStar,
  memo: true,
});
